/**
 * Analysis section for one continuous signal:
 * quantile portfolio sorts (bar chart + spread stats) and IC time series.
 */
import React, { useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  AlphaVal, BENCH_LABELS, HORIZON_LABELS, SigBadge, StatCard, ToggleGroup,
} from "./common";

const ChartTooltip = ({ active, payload, label, unit = "%" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border rounded-md px-3 py-2 text-xs shadow-md">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="font-mono">
          {p.value > 0 ? "+" : ""}{Number(p.value).toFixed(unit === "%" ? 2 : 4)}{unit}
        </div>
      ))}
    </div>
  );
};

export const ContinuousSignalSection = ({ block, horizons, testIdPrefix }) => {
  const [bench, setBench] = useState("sector");
  const [horizon, setHorizon] = useState(63);

  const key = `${bench}_${horizon}`;
  const qa = block?.quantiles?.[key];
  const ic = block?.ic?.[key];

  const chartData = useMemo(
    () =>
      (qa?.quantiles || []).map((q) => ({
        name: `Q${q.quantile}`,
        alpha: q.mean_alpha_pct,
        t: q.fm_tstat,
        hit: q.hit_rate_pct,
        n: q.n_obs,
      })),
    [qa]
  );

  const icSeries = useMemo(
    () => (ic?.series || []).map((s) => ({ date: s.date, ic: s.ic })),
    [ic]
  );

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-section`}>
      {/* Toggles */}
      <div className="flex items-center gap-3 flex-wrap">
        <ToggleGroup
          testId={`${testIdPrefix}-bench`}
          options={[
            { value: "sector", label: BENCH_LABELS.sector },
            { value: "spy", label: BENCH_LABELS.spy },
          ]}
          value={bench}
          onChange={setBench}
        />
        <ToggleGroup
          testId={`${testIdPrefix}-horizon`}
          options={horizons.map((h) => ({ value: h, label: HORIZON_LABELS[h] || `${h}d` }))}
          value={horizon}
          onChange={setHorizon}
        />
      </div>

      {!qa && !ic && (
        <div className="text-sm text-muted-foreground py-6 text-center">
          Insufficient data for this benchmark / horizon combination.
        </div>
      )}

      {qa && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Quantile bar chart */}
          <div className="lg:col-span-3">
            <div className="text-sm font-medium mb-2">
              Forward alpha by signal quintile{" "}
              <span className="text-muted-foreground font-normal">
                (Fama-MacBeth mean, {HORIZON_LABELS[horizon]}, {BENCH_LABELS[bench]})
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(128,128,128,0.08)" }} />
                <ReferenceLine y={0} stroke="currentColor" opacity={0.3} />
                <Bar dataKey="alpha" radius={[3, 3, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.alpha >= 0 ? "#10b981" : "#ef4444"} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-5 text-center text-xs text-muted-foreground mt-1">
              {chartData.map((d) => (
                <div key={d.name}>
                  t={d.t ?? "-"} · hit {d.hit ?? "-"}%
                </div>
              ))}
            </div>
          </div>

          {/* Spread stats */}
          <div className="lg:col-span-2 space-y-3">
            <div className="border rounded-lg p-4 space-y-2" data-testid={`${testIdPrefix}-spread-card`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Q5 − Q1 Long-Short Spread</span>
                <SigBadge t={qa.spread?.nw_tstat} />
              </div>
              <div className="text-3xl font-bold">
                <AlphaVal value={qa.spread?.mean_pct} />
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  per {HORIZON_LABELS[horizon]}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>NW t-stat: <span className="font-mono text-foreground">{qa.spread?.nw_tstat ?? "-"}</span></div>
                <div>Dates positive: <span className="font-mono text-foreground">{qa.spread?.pct_positive ?? "-"}%</span></div>
                <div className="col-span-2">
                  95% CI: <span className="font-mono text-foreground">
                    [{qa.spread?.ci_lo_pct ?? "-"}, {qa.spread?.ci_hi_pct ?? "-"}]%
                  </span>
                  <span className="ml-2">n={qa.spread?.n_dates} dates</span>
                </div>
              </div>
            </div>

            {ic && (
              <div className="grid grid-cols-2 gap-2">
                <StatCard label="Mean IC (Spearman)" testId={`${testIdPrefix}-ic-mean`}>
                  <AlphaVal value={ic.mean_ic} digits={4} suffix="" />
                </StatCard>
                <StatCard label="IC IR (mean/std)">
                  <span className="font-mono">{ic.ic_ir ?? "-"}</span>
                </StatCard>
                <StatCard label="IC NW t-stat">
                  <span className="font-mono flex items-center gap-2">
                    {ic.nw_tstat ?? "-"} <SigBadge t={ic.nw_tstat} />
                  </span>
                </StatCard>
                <StatCard label="% Dates IC > 0">
                  <span className="font-mono">{ic.pct_positive ?? "-"}%</span>
                </StatCard>
              </div>
            )}
          </div>
        </div>
      )}

      {/* IC time series */}
      {icSeries.length > 3 && (
        <div>
          <div className="text-sm font-medium mb-2">
            Cross-sectional IC over time{" "}
            <span className="text-muted-foreground font-normal">(per rebalance date)</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={icSeries} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<ChartTooltip unit="" />} />
              <ReferenceLine y={0} stroke="currentColor" opacity={0.3} />
              <Line type="monotone" dataKey="ic" stroke="#6366f1" strokeWidth={1.5} dot={{ r: 1.5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
